import {
  createNewImageMarker,
  Place,
  TUserPlaceType,
  useGeoveloMap,
  useLayers,
  UserPlace,
  useSource,
} from '@geovelo-frontends/commons';
import { Add } from '@mui/icons-material';
import {
  Box,
  Card,
  CardActions,
  CardHeader,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Marker } from 'maplibre-gl';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../app/context';

import { Button } from './button';
import { EditIcon, RemoveIcon } from './icons';
import PlaceDialog from './place-dialog';
import { placeTypes } from './place-dialog/form';
import RemovePlaceDialog from './remove-place-dialog';

const radiusSourceId = 'radius';

export function PlaceData({
  disableEdition,
  enableMap,
  placeType,
  label,
  selectedPlace,
  removable,
  onEdit,
}: {
  disableEdition?: boolean;
  enableMap?: boolean;
  label: string;
  onEdit?: () => void;
  placeType: Extract<TUserPlaceType, 'home' | 'work'> | 'company';
  removable?: boolean;
  selectedPlace: UserPlace | Place | null | undefined;
}): React.JSX.Element {
  const [initialized, setInitialized] = useState(false);
  const [sourcesInitialized, setSourcesInitialized] = useState(false);
  const [placeDialogOpen, openPlaceDialog] = useState(false);
  const [removeDialogOpen, openRemoveDialog] = useState(false);
  const {
    user: { commutingTrips },
  } = useContext(AppContext);
  const markerRef = useRef<Marker | null>(null);
  const { t } = useTranslation();
  const {
    map,
    initialized: mapInitialized,
    init: initMap,
    destroy: destroyMap,
  } = useGeoveloMap({
    smallDevice: true,
    baseLayer: 'geovelo',
  });
  const {
    addGeoJSONSource: addRadiusSource,
    updateGeoJSONSource: updateRadiusSource,
    getGeoJSONSource: getRadiusSource,
    clearGeoJSONSource: clearRadiusSource,
  } = useSource(map, radiusSourceId);
  const { addCircleLayer } = useLayers(map);

  useEffect(() => {
    setInitialized(true);

    return () => {
      if (enableMap && map) {
        destroyMap();
        setInitialized(false);
        setSourcesInitialized(false);
      }
    };
  }, []);

  useEffect(() => {
    if (initialized && selectedPlace && enableMap) {
      const {
        point: {
          coordinates: [lng, lat],
        },
      } = selectedPlace;

      if (!map)
        initMap({
          container: `place-${selectedPlace.id}-map`,
          center: { lat, lng },
          zoom: 11.8,
          maxZoom: 20,
          disableAttributionControl: true,
          interactive: false,
        });
      else map.flyTo({ center: { lat, lng }, zoom: 11.8, animate: false });
    }
  }, [initialized, selectedPlace]);

  useEffect(() => {
    if (mapInitialized) {
      if (!getRadiusSource()) {
        addRadiusSource();

        addCircleLayer('radius', radiusSourceId, {
          'circle-radius': [
            'interpolate',
            ['exponential', 2],
            ['zoom'],
            0,
            0,
            20,
            ['get', 'metersToPixelsAtMaxZoom'],
          ],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.2,
          'circle-stroke-color': ['get', 'color'],
          'circle-stroke-opacity': 0,
        });
      }

      setSourcesInitialized(true);
    }
  }, [mapInitialized]);

  useEffect(() => {
    if (map && sourcesInitialized && selectedPlace) {
      const { point } = selectedPlace;
      const {
        coordinates: [lng, lat],
      } = point;
      const metersToPixelsAtMaxZoom = 500 / (78271.484 / 2 ** 20) / Math.cos((lat * Math.PI) / 180);

      const { icon, color } = placeTypes[placeType];

      updateRadiusSource({
        type: 'FeatureCollection',
        features: [
          { type: 'Feature', geometry: point, properties: { color, metersToPixelsAtMaxZoom } },
        ],
      });

      markerRef.current = createNewImageMarker({
        color,
        icon,
        round: true,
        height: 20,
      })
        .setLngLat({ lat, lng })
        .addTo(map);
    }

    return () => {
      clearRadiusSource();
      if (markerRef.current) markerRef.current.remove();
    };
  }, [sourcesInitialized, selectedPlace]);

  if (selectedPlace === null) {
    return (
      <>
        <Box
          alignItems="center"
          bgcolor="#F6F8FC"
          borderRadius={4}
          display="flex"
          flexDirection="column"
          gap={2}
          padding={5}
        >
          <Typography align="center">
            {t('companies.pages.admin.user.address_form.empty_state')}
          </Typography>
          <Button
            color="primary"
            onClick={() => openPlaceDialog(true)}
            size="medium"
            startIcon={<Add />}
            variant="outlined"
          >
            {t('companies.pages.admin.user.address_form.actions.add')}
          </Button>
        </Box>
        <PlaceDialog
          currentPlace={null}
          onClose={() => openPlaceDialog(false)}
          open={placeDialogOpen}
          placeType={placeType}
        />
      </>
    );
  }

  return (
    <>
      <Card
        sx={{ alignItems: 'flex-start', borderRadius: 4, display: 'flex', flexDirection: 'row' }}
        variant="outlined"
      >
        {enableMap && selectedPlace ? (
          <Box
            bgcolor="#eee"
            borderRadius={2}
            flexShrink={0}
            height={80}
            id={`place-${selectedPlace.id}-map`}
            marginLeft={2}
            marginY={2}
            width={80}
          />
        ) : (
          enableMap && (
            <Skeleton height={80} sx={{ borderRadius: 2 }} variant="rounded" width={80} />
          )
        )}
        <CardHeader
          slotProps={{
            subheader: { variant: 'caption' },
            title: { fontWeight: 700, variant: 'body2' },
          }}
          subheader={
            selectedPlace ? selectedPlace.address : <Skeleton variant="text" width={200} />
          }
          sx={{ alignSelf: 'center', flexGrow: 1 }}
          title={
            (placeType === 'work' &&
              selectedPlace &&
              'title' in selectedPlace &&
              selectedPlace.title) ||
            label
          }
        />
        {(!disableEdition ||
          !selectedPlace ||
          (removable && 'type' in selectedPlace && selectedPlace.type === 'work')) && (
          <CardActions sx={{ flexShrink: 0 }}>
            {selectedPlace ? (
              <>
                {!disableEdition && (
                  <Tooltip
                    placement="top"
                    title={t('companies.pages.admin.user.address_form.actions.update')}
                  >
                    <IconButton
                      color="primary"
                      onClick={() => onEdit?.() || openPlaceDialog(true)}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {removable && 'type' in selectedPlace && selectedPlace.type === 'work' && (
                  <Tooltip placement="top" title={t('commons.actions.remove')}>
                    <IconButton
                      color="error"
                      disabled={!commutingTrips}
                      onClick={() => openRemoveDialog(true)}
                      size="small"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : (
              <>
                {!disableEdition && <Skeleton height={30} variant="circular" width={30} />}
                {removable && <Skeleton height={30} variant="circular" width={30} />}
              </>
            )}
          </CardActions>
        )}
      </Card>
      {selectedPlace && (
        <>
          <PlaceDialog
            currentPlace={selectedPlace}
            onClose={() => openPlaceDialog(false)}
            open={placeDialogOpen}
            placeType={placeType}
          />
          {'type' in selectedPlace && (
            <RemovePlaceDialog
              onClose={() => openRemoveDialog(false)}
              open={removeDialogOpen}
              place={selectedPlace}
            />
          )}
        </>
      )}
    </>
  );
}
